//src/pages/Clips.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api";
import { normalizeMediaUrl } from "../lib/media";

import { registerClipView } from "../lib/registerClipView";

const iconBtn = {
  background: "rgba(0,0,0,0.4)",
  border: "none",
  color: "#fff",
  fontSize: 20,
  borderRadius: "50%",
  width: 44,
  height: 44,
  cursor: "pointer",
};

const count = {
  fontSize: 12,
  color: "#fff",
  marginTop: -8,
};

function getMediaArray(post) {
  if (Array.isArray(post.media_urls)) return post.media_urls;
  try {
    return JSON.parse(post.media_urls || "[]");
  } catch {
    return [];
  }
}

function getVideoUrl(post) {
  const media = getMediaArray(post);
  const video = media.find((m) => {
    const url = normalizeMediaUrl(m || "").toLowerCase();
    return /\.(mp4|webm|ogg)(\?.*)?$/i.test(url);
  });
  return video ? normalizeMediaUrl(video) : null;
}

function getHashtags(text = "") {
  const matches = text.match(/#[\w]+/g);
  return matches || [];
}

function getLocationName(post) {
  return (
    post.location_name ||
    post.location?.name ||
    post.footprint_name ||
    ""
  );
}

export default function Clips() {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const clipId = query.get("clip");

  const [activeTab, setActiveTab] = useState("recent");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  

  useEffect(() => {
    let alive = true;
    setLoading(true);

    (async () => {
      try {
        const tabValue = activeTab === "interest" ? "interest" : activeTab;
        const { data } = await api.get(`/feed?tab=${tabValue}&limit=50`);

        if (!alive) return;
        setPosts(data?.posts || []);
      } catch (e) {
        console.error("clips load error", e?.response?.data || e.message);
        if (alive) setPosts([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [activeTab]);
  
  
  
  const handleSupport = async (clipId) => {

  // optimistic update
  setPosts((prev) =>
    prev.map((p) => {
      if (p.id !== clipId) return p;

      const supported = !!p.is_supported;

      return {
        ...p,
        is_supported: !supported,
        support_count: supported
          ? Math.max(0, (p.support_count || 1) - 1)
          : (p.support_count || 0) + 1,
      };
    })
  );

  try {
    await api.post(`/posts/${clipId}/support`);
  } catch (e) {
    console.error("support failed", e);

    // rollback
    setPosts((prev) =>
      prev.map((p) => {
        if (p.id !== clipId) return p;

        const supported = !!p.is_supported;

        return {
          ...p,
          is_supported: !supported,
          support_count: supported
            ? (p.support_count || 0) + 1
            : Math.max(0, (p.support_count || 1) - 1),
        };
      })
    );
  }
};

    

const handleRepost = async (clipId) => {
  try {
    await api.post(`/posts/${clipId}/repost`);

    setPosts((prev) =>
      prev.map((p) =>
        p.id === clipId
          ? { ...p, repost_count: (p.repost_count || 0) + 1 }
          : p
      )
    );
  } catch (e) {
    console.error("repost failed", e);
  }
};

const handleShare = (clipId) => {
  const url = `${window.location.origin}/clips?clip=${clipId}`;

  if (navigator.share) {
    navigator.share({ url });
  } else {
    navigator.clipboard.writeText(url);
    alert("Link copied!");
  }
};

  const clips = useMemo(() => {
    return posts.filter((p) => {
      const media = getMediaArray(p);

      const hasVideo = media.some((m) => {
        const url = normalizeMediaUrl(m || "").toLowerCase();
        return /\.(mp4|webm|ogg)(\?.*)?$/i.test(url);
      });

      return (
        p.type === "clip" ||
        p.post_type === "clip" ||
        p.is_clip === true ||
        hasVideo
      );
    });
  }, [posts]);

  const startIndex = Math.max(
    0,
    clips.findIndex((p) => String(p.id) === String(clipId))
  );

  const visibleClips = clipId ? clips.slice(startIndex) : clips;
  
  
  
  if (loading) {
  return (
    <div style={{ color: "#fff", padding: 20 }}>
      Loading clips...
    </div>
  );
}

  return (
  <div
    
    style={{
      height: "100dvh",
      width: "100%",
      overflowY: "scroll",
      overflowX: "hidden", // ✅ ADD THIS
      scrollSnapType: "y mandatory",
      background: "#000",
      userSelect: "none",
      touchAction: "pan-y pinch-zoom",
      overscrollBehaviorY: "contain",
      WebkitOverflowScrolling: "touch",
    }}
  >
    {visibleClips.map((clip) => {
      const videoUrl = getVideoUrl(clip);
      if (!videoUrl) return null;

      const caption = clip.text || "";
const hashtags = getHashtags(caption);
const locationName = getLocationName(clip);

      return (
        <div
  key={clip.id}
  data-clip-id={clip.id}
  
  style={{
            height: "100dvh",
            width: "100%",
            position: "relative",
            scrollSnapAlign: "start", 
            scrollSnapStop: "always",
            background: "#000",
          }}
        >
          {/* VIDEO */}
          <video
  ref={(video) => {
    if (!video) return;

    registerClipView(video, clip.id, (viewCount) => {
      setPosts(prev =>
        prev.map(p =>
          p.id === clip.id
            ? { ...p, view_count: viewCount }
            : p
        )
      );
    });
  }}
  src={videoUrl}
  playsInline
  muted={isMuted}
  loop
  autoPlay
  preload="metadata"
  onClick={(e) => {
    if (e.target.paused) e.target.play();
    else e.target.pause();
  }}
  style={{
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transform: "scale(1.01)",
    willChange: "transform",
  }}
/>

 <div
  style={{
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "35%",
    background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
  }}
/>

          {/* RIGHT SIDE BUTTONS */}
          
          <div
            style={{
              position: "absolute",
              right: 12,
              bottom: "max(120px, env(safe-area-inset-bottom))",
              display: "flex",
              flexDirection: "column",
              gap: 16,
              alignItems: "center",
            }}
          >
            <button
  style={iconBtn}
  onClick={() => setIsMuted((prev) => !prev)}
>
  {isMuted ? "🔇" : "🔊"}
</button>
            
            {/* ❤️ LIKE */}
<button
  style={{
    ...iconBtn,
    color: clip.is_supported ? "#22c55e" : "#fff",
    transform: clip.is_supported ? "scale(1.2)" : "scale(1)",
    transition: "all 0.2s ease",
  }}
  onClick={() => handleSupport(clip.id)}
>
  ⬆️
</button>
<div style={count}>{clip.support_count || 0}</div>

{/* 💬 COMMENT */}
<button
  style={iconBtn}
  onClick={() => navigate(`/clips/${clip.id}`)}
>
  💬
</button>
<div style={count}>{clip.comment_count || 0}</div>

{/* 🔁 REPOST */}
<button
  style={iconBtn}
  onClick={() => handleRepost(clip.id)}
>
  🔁
</button>
<div style={count}>{clip.repost_count || 0}</div>

{/* ↗ SHARE */}
<button
  style={iconBtn}
  onClick={() => handleShare(clip.id)}
>
  ↗
</button>

<div style={count}>👁 {clip.view_count || 0}</div>
          </div>
          
          {/* CAPTION */}
          <div
            style={{
              position: "absolute",
              left: 12,
              bottom: "max(120px, env(safe-area-inset-bottom))",
              right: 80,
              color: "#fff",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              @{clip.user_id || clip.username}
            </div>

            <div style={{ fontSize: 14, opacity: 0.9 }}>
              {caption}
            </div>

            <div style={{ marginTop: 6, fontSize: 13, color: "#93c5fd" }}>
  {hashtags.join(" ")}
</div>

{locationName && (
  <div
    style={{
      marginTop: 8,
      fontSize: 13,
      color: "#fff",
      opacity: 0.9,
    }}
  >
    📍 {locationName}
  </div>
)}
          </div>
        </div>
      );
    })}
    
    
    
  </div>
);
}